import type { Appointment, UpdateTechnicianCommentDto } from "@myapp/contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { appointmentKeys } from "../appointment.keys";
import { scheduleKeys } from "../../../schedule/api/schedule.keys";
import { workOrderKeys } from "../../../work-order/api/work-order.keys";
import { auditLogKeys } from "../../../audit-log";
import { appointmentApi } from "../service";

/**
 * The technician's comment, from the worker detail page. Invalidates the same three readers
 * `useCompleteAppointment` does — the board's hover card and the work-order table both show it.
 */
export function useUpdateAppointmentTechnicianComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      dto,
      workOrderId,
    }: {
      id: number;
      dto: UpdateTechnicianCommentDto;
      workOrderId?: string;
    }) => {
      const { data } = await appointmentApi.updateTechnicianComment(id, dto);
      return { appointment: data as Appointment, workOrderId };
    },
    onSuccess: ({ appointment }) => {
      // The response is the full appointment — seed the detail so the card reflects the save
      // before the refetch lands.
      queryClient.setQueryData(appointmentKeys.detail(appointment.id), (prev: Appointment | undefined) =>
        prev ? { ...prev, technicianComment: appointment.technicianComment } : prev,
      );
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      if (vars?.workOrderId) {
        queryClient.invalidateQueries({ queryKey: auditLogKeys.byWorkOrder(vars.workOrderId) });
      }
    },
  });
}
